import React, { Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import i18n from "i18next";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import ProtectedRoute from "@/components/ProtectedRoute";
import NotFoundPage from "@/pages/NotFoundPage";
import { Toaster } from "sonner";
import PaywallModal from "@/components/PaywallModal";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import CookieBanner from "@/components/CookieBanner";
import AppSplash from "@/components/mobile/AppSplash";
import BottomTabBar from "@/components/mobile/BottomTabBar";
import ConfirmHost from "@/components/mobile/ConfirmHost";
import PageTransition from "@/components/mobile/PageTransition";
import { useIsMobile } from "@/hooks/useMediaQuery";

// Everything but the entry screens (landing, login, dashboard) is split out of the main
// bundle and fetched on demand: the app starts faster, which matters most on a phone's
// connection. Heavy screens are prefetched while the browser is idle (see PrefetchRoutes).
const loaders = {
  ProjectView: () => import("@/pages/ProjectView"),
  PublicShare: () => import("@/pages/PublicShare"),
  AdminPage: () => import("@/pages/AdminPage"),
  LegalPage: () => import("@/pages/LegalPage"),
  PricingPage: () => import("@/pages/PricingPage"),
  TeamPage: () => import("@/pages/TeamPage"),
  JoinTeamPage: () => import("@/pages/JoinTeamPage"),
  SupportPage: () => import("@/pages/SupportPage"),
  BlogIndexPage: () => import("@/pages/BlogIndexPage"),
  BlogPostPage: () => import("@/pages/BlogPostPage"),
  UseCasePage: () => import("@/pages/UseCasePage"),
  PaymentPages: () => import("@/pages/PaymentPages"),
  ActivateAccountPage: () => import("@/pages/ActivateAccountPage"),
  ConfirmEmailPage: () => import("@/pages/ConfirmEmailPage"),
};
const ProjectView = React.lazy(loaders.ProjectView);
const PublicShare = React.lazy(loaders.PublicShare);
const AdminPage = React.lazy(loaders.AdminPage);
const LegalPage = React.lazy(loaders.LegalPage);
const PricingPage = React.lazy(loaders.PricingPage);
const TeamPage = React.lazy(loaders.TeamPage);
const JoinTeamPage = React.lazy(loaders.JoinTeamPage);
const SupportPage = React.lazy(loaders.SupportPage);
const BlogIndexPage = React.lazy(loaders.BlogIndexPage);
const BlogPostPage = React.lazy(loaders.BlogPostPage);
const UseCasePage = React.lazy(loaders.UseCasePage);
const ActivateAccountPage = React.lazy(loaders.ActivateAccountPage);
const ConfirmEmailPage = React.lazy(loaders.ConfirmEmailPage);
const PaymentSuccess = React.lazy(() => loaders.PaymentPages().then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = React.lazy(() => loaders.PaymentPages().then((m) => ({ default: m.PaymentCancel })));

function GlobalPaywallHost() {
  const [state, setState] = React.useState({ open: false, action: "default", message: "" });
  React.useEffect(() => {
    const handler = (e) => {
      const { action, message } = e.detail || {};
      setState({ open: true, action: action || "default", message: message || "" });
    };
    window.addEventListener("flowgantt:paywall", handler);
    return () => window.removeEventListener("flowgantt:paywall", handler);
  }, []);
  return (
    <PaywallModal
      open={state.open}
      onOpenChange={(v) => setState((s) => ({ ...s, open: v }))}
      action={state.action}
      message={state.message}
    />
  );
}

// Toasts drop from the top on phones (below the status bar, clear of the tab bar / FABs).
function AppToaster() {
  const isMobile = useIsMobile();
  return (
    <Toaster
      position={isMobile ? "top-center" : "top-right"}
      mobileOffset={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)", left: 12, right: 12 }}
      richColors
    />
  );
}

// Once signed in, warm up the screens people are most likely to open next, when idle.
function PrefetchRoutes() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    // Respect data-saver mode and very slow connections.
    const conn = navigator.connection;
    if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ""))) return undefined;
    const idle = window.requestIdleCallback || ((cb) => window.setTimeout(cb, 2000));
    const cancel = window.cancelIdleCallback || window.clearTimeout;
    const handle = idle(() => {
      [loaders.ProjectView, loaders.TeamPage, loaders.SupportPage].forEach((load) => load().catch(() => {}));
    });
    return () => cancel(handle);
  }, [user]);
  return null;
}

// A failed chunk (offline, or a new deploy replaced the old files) must not leave a blank screen.
class RouteErrorBoundary extends React.Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4 bg-white px-6 text-center">
        <div className="font-display text-xl font-black tracking-tight">FlowGantt</div>
        <p className="max-w-xs text-sm text-neutral-500">{i18n.t("mobile.routeError.message")}</p>
        <button
          type="button"
          data-testid="route-reload-btn"
          onClick={() => window.location.reload()}
          className="h-11 rounded-xl bg-neutral-950 px-6 text-sm font-semibold text-white"
        >
          {i18n.t("mobile.routeError.retry")}
        </button>
      </div>
    );
  }
}

function AppRouter() {
  return (
    <PageTransition>
    <RouteErrorBoundary>
    <Suspense fallback={<AppSplash testId="route-loading" />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<LoginPage mode="register" />} />
      <Route path="/activate" element={<ActivateAccountPage />} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/project/:projectId"
        element={
          <ProtectedRoute>
            <ProjectView />
          </ProtectedRoute>
        }
      />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/share/:shareId" element={<PublicShare />} />
      <Route path="/legal" element={<Navigate to="/legal/mentions" replace />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/blog" element={<BlogIndexPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/pour/:slug" element={<UseCasePage />} />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamPage />
          </ProtectedRoute>
        }
      />
      <Route path="/join-team/:token" element={<JoinTeamPage />} />
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/support/:ticketId"
        element={
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
    </RouteErrorBoundary>
    </PageTransition>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <PrefetchRoutes />
          <AppRouter />
          <GlobalPaywallHost />
          <BottomTabBar />
          <ConfirmHost />
          <CookieBanner />
          <AppToaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
